/**
 * Resolve the visualizer "test mode" flag (exposes test-only plot modes).
 *
 * Activated by a `?test=1` (or `?test=true`) URL query param and then PERSISTED
 * to `localStorage`, because a URL param can't survive a reload in this app:
 * the Auth0 client uses the default in-memory cache (no refresh tokens), so a
 * full page reload drops the session and forces a re-auth whose fixed
 * `redirect_uri` (`${origin}/`) strips the query string. Persisting the flag
 * means the user activates it once; it then sticks across reloads and the auth
 * redirect cycle. `?test=0` (or `false`) turns it back off.
 *
 * Called once in `main.ts` (before bootstrap, so the flag is captured even when
 * the first `?test=1` load lands on the logged-out `<home>` screen and the main
 * view never mounts) and again wherever the current value is needed.
 */
const TEST_MODE_KEY = 'jaxviz.testMode';

function readStoredTestMode(): boolean {
  try {
    return localStorage.getItem(TEST_MODE_KEY) === '1';
  } catch {
    return false; // localStorage unavailable (private mode / disabled)
  }
}

export function resolveTestMode(): boolean {
  const raw = new URLSearchParams(window.location.search).get('test');
  if (raw === null) {
    return readStoredTestMode(); // no param → whatever was last persisted
  }
  const on = ['1', 'true'].includes(raw.trim().toLowerCase());
  try {
    if (on) localStorage.setItem(TEST_MODE_KEY, '1');
    else localStorage.removeItem(TEST_MODE_KEY);
  } catch {
    /* couldn't persist — still honour the param for this page load */
  }
  return on;
}
